import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  vaultScopeKey,
  vaultSubjectUserId,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import { useTRPC } from "@/trpc";

type PreviewState = {
  key: string;
  url: string | null;
  error: string | null;
};

/** Keep preview grants local to the mounted editor. A slow response for a
 * previously selected file must never replace the document now on screen. */
export function useCredentialEvidencePreview({
  scope,
  documentId,
  isReviewDocument,
}: {
  scope: CredentialVaultScope;
  documentId: string;
  isReviewDocument: boolean;
}) {
  const trpc = useTRPC();
  const userId = vaultSubjectUserId(scope);
  const organizationBusinessUnitId =
    scope.kind === "organization" ? scope.organizationBusinessUnitId : "";
  const expectedAffiliationId =
    scope.kind === "organization" ? scope.expectedAffiliationId : undefined;
  const isOrganization = scope.kind === "organization";
  const { mutateAsync: openHeldDocument } = useMutation(
    trpc.credentials.getMyDocumentUrl.mutationOptions(),
  );
  const { mutateAsync: openReviewDocument } = useMutation(
    trpc.credentials.getMyReviewDocumentUrl.mutationOptions(),
  );
  const { mutateAsync: openOrgHeldDocument } = useMutation(
    trpc.credentials.getDocumentUrlForBu.mutationOptions(),
  );
  const { mutateAsync: openOrgReviewDocument } = useMutation(
    trpc.credentials.getReviewDocumentUrlForBu.mutationOptions(),
  );
  const [state, setState] = useState<PreviewState | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = `${vaultScopeKey(scope)}/${documentId}/${isReviewDocument}/${attempt}`;

  useEffect(() => {
    let current = true;
    function mintUrl() {
      if (isOrganization) {
        const openDocument = isReviewDocument
          ? openOrgReviewDocument
          : openOrgHeldDocument;
        return openDocument({
          documentId,
          organizationBusinessUnitId,
          providerUserId: userId,
          ...(expectedAffiliationId ? { expectedAffiliationId } : {}),
          asAttachment: false,
        });
      }
      const openDocument = isReviewDocument
        ? openReviewDocument
        : openHeldDocument;
      return openDocument({
        documentId,
        switchUserId: userId,
        asAttachment: false,
      });
    }
    void mintUrl()
      .then((result) => {
        if (current) {
          setState({ key, url: result.signedUrl, error: null });
        }
      })
      .catch((error: unknown) => {
        if (!current) {
          return;
        }
        setState({
          key,
          url: null,
          error:
            error instanceof Error
              ? error.message
              : "Could not load this document.",
        });
      });
    return () => {
      current = false;
    };
  }, [
    documentId,
    userId,
    isReviewDocument,
    openHeldDocument,
    openReviewDocument,
    openOrgHeldDocument,
    openOrgReviewDocument,
    organizationBusinessUnitId,
    expectedAffiliationId,
    isOrganization,
    key,
  ]);

  const current = state?.key === key ? state : null;
  return {
    url: current?.url ?? null,
    error: current?.error ?? null,
    isLoading: current === null,
    retry: () => setAttempt((value) => value + 1),
  };
}
